const baseUrl = import.meta.env.VITE_API_BASE;

if (!baseUrl) {
    throw new Error("VITE_API_BASE is not set.");
}

// ── Domain types ────────────────────────────────────────────────────────────

export type TenantPlan = "Starter" | "Business" | "Enterprise";
export type TenantStatus = "Pending" | "Active" | "Suspended" | "Cancelled";
export type InvoiceStatus = "PendingPayment" | "Paid" | "Cancelled";
export type DnsRecordStatus = "Unknown" | "Verified" | "Failed" | "NotConfigured";

export interface TenantSummary {
    id: string;
    companyName: string;
    domain: string;
    plan: TenantPlan;
    maxUsers: number | null;
    status: TenantStatus;
    dnsStatus: DnsRecordStatus;
    createdAt: string;
    activatedAt: string | null;
}

export interface Tenant extends TenantSummary {
    adminEmail: string;
    adminName: string;
    phone: string | null;
    address: string | null;
    enabledApps: string[];
    notes: string | null;
    updatedAt: string;
    domainVerifiedAt: string | null;
    identityTenantId: string | null;
}

export interface InvoiceSummary {
    id: string;
    invoiceNumber: string;
    tenantId: string;
    tenantName: string;
    amount: number;
    currency: string;
    status: InvoiceStatus;
    dueDate: string;
    paidAt: string | null;
}

export interface Invoice extends InvoiceSummary {
    lineItemsJson: string;
    notes: string | null;
    createdAt: string;
}

export interface DnsRecord {
    recordType: string;
    name: string;
    expectedValue: string;
    actualValue: string | null;
    status: DnsRecordStatus;
    errorMessage: string | null;
}

export interface DnsStatus {
    domain: string;
    checkedAt: string;
    records: DnsRecord[];
    allVerified: boolean;
}

export interface Dashboard {
    totalTenants: number;
    activeTenants: number;
    pendingTenants: number;
    suspendedTenants: number;
    pendingInvoiceCount: number;
    pendingInvoiceTotal: number;
    recentEnrollments: TenantSummary[];
}

export interface EnrollRequest {
    companyName: string;
    domain: string;
    adminEmail: string;
    adminName: string;
    phone?: string;
    address?: string;
    plan: TenantPlan;
    maxUsers?: number;
    enabledApps?: string[];
    notes?: string;
}

export interface EnrollResponse {
    tenantId: string;
    invoiceNumber: string;
    amount: number;
    currency: string;
    dueDate: string;
    dnsRecords: DnsRecord[];
}

export interface LoginResponse {
    token: string;
    expiresAt: string;
}

export interface UpdateTenantRequest {
    companyName?: string;
    adminEmail?: string;
    adminName?: string;
    phone?: string | null;
    address?: string | null;
    notes?: string | null;
    enabledApps?: string[];
}

// ── HTTP helper ──────────────────────────────────────────────────────────────

async function fetchJson<T>(
    path: string,
    token: string | null,
    init: RequestInit = {}
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);

    const url = path.startsWith("http") ? path : `${baseUrl}${path}`;
    const res = await fetch(url, { ...init, headers });
    if (!res.ok) {
        if (res.status === 401) {
            window.dispatchEvent(new Event("portal-unauthorized"));
        }
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`Portal API ${path} → ${res.status}: ${text}`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

// ── API functions ────────────────────────────────────────────────────────────

export function login(username: string, password: string) {
    return fetchJson<LoginResponse>("/api/auth/login", null, {
        method: "POST",
        body: JSON.stringify({ username, password })
    });
}

export function getDashboard(token: string | null) {
    return fetchJson<Dashboard>("/api/portal/dashboard", token);
}

export function getTenants(token: string | null, status?: TenantStatus) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return fetchJson<TenantSummary[]>(`/api/portal/tenants${qs}`, token);
}

export function getTenant(token: string | null, id: string) {
    return fetchJson<Tenant>(`/api/portal/tenants/${id}`, token);
}

export interface LicenseSummary {
    tenantId: string;
    plan: string;
    totalSeats: number;
    usedSeats: number;
    freeSeats: number;
    pricePerSeatMonthly: number;
    currency: string;
    enabledApps: string[];
}

export function getLicenseSummary(token: string | null, id: string) {
    return fetchJson<LicenseSummary>(`/api/portal/tenants/${id}/license`, token);
}

export function updateTenant(token: string | null, id: string, data: UpdateTenantRequest) {
    return fetchJson<Tenant>(`/api/portal/tenants/${id}`, token, {
        method: "PUT",
        body: JSON.stringify(data)
    });
}

export function activateTenant(token: string | null, id: string) {
    return fetchJson<Tenant>(`/api/portal/tenants/${id}/activate`, token, { method: "POST" });
}

export function suspendTenant(token: string | null, id: string) {
    return fetchJson<Tenant>(`/api/portal/tenants/${id}/suspend`, token, { method: "POST" });
}

export function getInvoices(token: string | null, status?: InvoiceStatus, tenantId?: string) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (tenantId) params.set("tenantId", tenantId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return fetchJson<InvoiceSummary[]>(`/api/portal/invoices${qs}`, token);
}

export function getInvoice(token: string | null, id: string) {
    return fetchJson<Invoice>(`/api/portal/invoices/${id}`, token);
}

export function markInvoicePaid(token: string | null, id: string) {
    return fetchJson<Invoice>(`/api/portal/invoices/${id}/mark-paid`, token, { method: "POST" });
}

export function getDnsStatus(token: string | null, tenantId: string) {
    return fetchJson<DnsStatus>(`/api/portal/tenants/${tenantId}/dns`, token);
}

export function verifyDns(token: string | null, tenantId: string) {
    return fetchJson<DnsStatus>(`/api/portal/tenants/${tenantId}/dns/verify`, token, { method: "POST" });
}

export function enroll(data: EnrollRequest) {
    return fetchJson<EnrollResponse>("/api/portal/enroll", null, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

// ── Plan / seat changes ──────────────────────────────────────────────────────

export interface ChangePlanRequest {
    plan?: TenantPlan;
    maxUsers?: number;
}

export interface ChangePlanResponse {
    tenant: Tenant;
    invoiceNumber: string;
    deltaAmount: number;
    currency: string;
}

export function changePlan(token: string | null, id: string, data: ChangePlanRequest) {
    return fetchJson<ChangePlanResponse>(`/api/portal/tenants/${id}/plan`, token, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

// ── Billing / plans ──────────────────────────────────────────────────────────

export interface Plan {
    id: string;
    name: string;
    description: string;
    maxUsers: number;
    maxStorageGb: number;
    priceMonthlyInr: number;
    priceAnnuallyInr: number;
    featuresJson: string;
}

export interface CheckoutResponse {
    orderId: string;
    keyId: string;
    amountInPaise: number;
    currency: string;
    planName: string;
    cycle: "Monthly" | "Annually";
    seatCount: number;
}

export interface Subscription {
    id: string;
    tenantId: string;
    planId: string;
    planName: string;
    status: string;
    cycle: string;
    seatCount: number;
    periodStart: string;
    periodEnd: string;
    priceMonthlyInr: number;
    priceAnnuallyInr: number;
}

export function getPlans() {
    return fetchJson<Plan[]>("/api/billing/plans", null);
}

export function createCheckout(
    token: string | null,
    planId: string,
    cycle: "Monthly" | "Annually",
    seatCount: number
) {
    return fetchJson<CheckoutResponse>("/api/billing/checkout", token, {
        method: "POST",
        body: JSON.stringify({ planId, cycle, seatCount })
    });
}

export function verifyPayment(
    token: string | null,
    razorpayOrderId: string,
    razorpayPaymentId: string,
    razorpaySignature: string
) {
    return fetchJson<{ subscriptionId: string; status: string; validUntil: string }>(
        "/api/billing/verify",
        token,
        { method: "POST", body: JSON.stringify({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) }
    );
}

export function getSubscription(token: string | null) {
    return fetchJson<Subscription | { status: string }>("/api/billing/subscription", token);
}

// ── Resellers ────────────────────────────────────────────────────────────────

export interface ResellerSummary {
    id: string;
    companyName: string;
    email: string;
    pricingTier: string;
    creditBalance: number;
    isActive: boolean;
    createdAt: string;
}

export interface Reseller extends ResellerSummary {
    phone: string | null;
    totalCreditsLoaded: number;
    totalCreditsUsed: number;
    notes: string | null;
    updatedAt: string;
}

export interface CreditLogEntry {
    id: string;
    type: string;
    amount: number;
    balanceAfter: number;
    description: string | null;
    reference: string | null;
    createdAt: string;
}

export function getResellers(token: string | null) {
    return fetchJson<ResellerSummary[]>("/api/admin/resellers", token);
}

export function getReseller(token: string | null, id: string) {
    return fetchJson<Reseller>(`/api/admin/resellers/${id}`, token);
}

export function createReseller(token: string | null, data: {
    companyName: string; email: string; phone?: string; pricingTier?: string; notes?: string;
}) {
    return fetchJson<Reseller>("/api/admin/resellers", token, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

export function topupResellerCredits(token: string | null, id: string, amount: number, description?: string) {
    return fetchJson<{ resellerId: string; creditBalance: number }>(
        `/api/admin/resellers/${id}/topup`,
        token,
        { method: "POST", body: JSON.stringify({ amount, description }) }
    );
}

export function getResellerCreditLog(token: string | null, id: string) {
    return fetchJson<{ items: CreditLogEntry[] }>(`/api/admin/resellers/${id}/credit-log`, token);
}

// ── Plugin API keys ──────────────────────────────────────────────────────────

export interface ApiKey {
    id: string;
    name: string;
    prefix: string;
    createdAt: string;
    lastUsedAt: string | null;
    expiresAt: string | null;
    resellerId?: string | null;
}

export interface CreateApiKeyResponse extends ApiKey {
    apiKey: string;
    warning: string;
}

export function getApiKeys(token: string | null) {
    return fetchJson<ApiKey[]>("/api/plugin/keys", token);
}

export function createApiKey(token: string | null, name: string, expiresInDays?: number) {
    return fetchJson<CreateApiKeyResponse>("/api/plugin/keys", token, {
        method: "POST",
        body: JSON.stringify({ name, expiresInDays })
    });
}

export function revokeApiKey(token: string | null, id: string) {
    return fetchJson<{ id: string; revoked: boolean }>(`/api/plugin/keys/${id}`, token, { method: "DELETE" });
}

export function createResellerApiKey(
    token: string | null,
    resellerId: string,
    name: string,
    expiresInDays?: number
) {
    return fetchJson<CreateApiKeyResponse>(`/api/admin/resellers/${resellerId}/api-keys`, token, {
        method: "POST",
        body: JSON.stringify({ name, expiresInDays })
    });
}

// ── Identity tenant link ─────────────────────────────────────────────────────

export interface IdentityTenantSummary {
    tenantId: string;
    name: string;
    subdomain: string;
    mailDomain: string | null;
    isActive: boolean;
}

export function getIdentityTenants(token: string | null) {
    return fetchJson<IdentityTenantSummary[]>("/api/portal/identity-tenants", token);
}

export function setIdentityLink(token: string | null, portalTenantId: string, identityTenantId: string | null) {
    return fetchJson<{ tenantId: string; identityTenantId: string | null }>(
        `/api/portal/tenants/${portalTenantId}/identity-link`,
        token,
        { method: "PUT", body: JSON.stringify({ identityTenantId }) }
    );
}

// ── Tenant users ─────────────────────────────────────────────────────────────

export interface TenantUser {
    userId: string;
    email: string;
    displayName: string | null;
    tenantId: string;
    roles: string[];
    createdAt: string;
    isLockedOut: boolean;
}

export interface CreateTenantUserRequest {
    email: string;
    displayName: string;
    password: string;
    role?: string;
}

export function getTenantUsers(token: string | null, tenantId: string) {
    return fetchJson<TenantUser[]>(`/api/portal/tenants/${tenantId}/users`, token);
}

export function createTenantUser(token: string | null, tenantId: string, data: CreateTenantUserRequest) {
    return fetchJson<TenantUser>(`/api/portal/tenants/${tenantId}/users`, token, {
        method: "POST",
        body: JSON.stringify(data)
    });
}

export function resetTenantUserPassword(token: string | null, tenantId: string, userId: string, newPassword: string) {
    return fetchJson<void>(`/api/portal/tenants/${tenantId}/users/${userId}/reset-password`, token, {
        method: "POST",
        body: JSON.stringify({ newPassword })
    });
}

export function deleteTenantUser(token: string | null, tenantId: string, userId: string) {
    return fetchJson<void>(`/api/portal/tenants/${tenantId}/users/${userId}`, token, { method: "DELETE" });
}
